/* Jouri components: initialise on navigation and Shopify editor section reloads. */
(() => {
  const cleanups = new WeakMap();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  function init(root) {
    if (cleanups.has(root)) return;
    const abort = new AbortController();
    const on = (el, event, callback) => el?.addEventListener(event, callback, { signal: abort.signal });
    let stop = () => {}, disposed = false;
    if (root.matches('[data-jouri-hero]')) {
      const slides = [...root.querySelectorAll('.j-slide-hero__slide')];
      const dots = [...root.querySelectorAll('[data-hero-dot]')];
      const pause = root.querySelector('[data-hero-pause]');
      const stage = root.querySelector('.j-slide-hero__stage');
      let index = 0, timer, paused = reduced.matches, hovering = false, editorSelected = false;
      const duration = (parseFloat(getComputedStyle(root).getPropertyValue('--j-hero-duration')) || 7) * 1000;
      stop = () => clearInterval(timer);
      function start() {
        stop();
        if (disposed || !root.isConnected || slides.length < 2 || paused || hovering || editorSelected || reduced.matches || document.hidden || root.contains(document.activeElement)) return;
        timer = setInterval(() => show(index + 1), duration);
      }
      function show(next) {
        index = (next + slides.length) % slides.length;
        stage.style.transform = `translateX(-${index * 100}%)`;
        slides.forEach((slide, i) => { slide.classList.toggle('is-active', i === index); slide.setAttribute('aria-hidden', String(i !== index)); slide.inert = i !== index; });
        dots.forEach((dot, i) => { dot.classList.toggle('is-active', i === index); dot.setAttribute('aria-pressed', String(i === index)); });
      }
      on(root.querySelector('[data-hero-prev]'), 'click', () => { show(index - 1); start(); });
      on(root.querySelector('[data-hero-next]'), 'click', () => { show(index + 1); start(); });
      dots.forEach(dot => on(dot, 'click', () => { show(Number(dot.dataset.heroDot)); start(); }));
      let gesture;
      on(stage, 'pointerdown', event => {
        if (event.pointerType !== 'touch' || !event.isPrimary) return;
        gesture = { x: event.clientX, y: event.clientY, id: event.pointerId };
        stage.setPointerCapture(event.pointerId);
        stop();
      });
      on(stage, 'pointerup', event => {
        if (!gesture || gesture.id !== event.pointerId) return;
        const dx = event.clientX - gesture.x, dy = event.clientY - gesture.y;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy) * 1.5) show(index + (dx < 0 ? 1 : -1));
        gesture = null;
        start();
      });
      on(stage, 'pointercancel', () => { gesture = null; start(); });
      on(pause, 'click', () => { paused = !paused; pause.setAttribute('aria-pressed', String(paused)); start(); });
      pause?.setAttribute('aria-pressed', String(paused));
      on(root, 'mouseenter', () => { hovering = true; stop(); });
      on(root, 'mouseleave', () => { hovering = false; start(); });
      on(root, 'focusin', stop);
      on(root, 'focusout', () => setTimeout(start, 0));
      on(document, 'visibilitychange', start);
      on(reduced, 'change', start);
      on(root, 'shopify:block:select', event => { const slide = event.target.closest('.j-slide-hero__slide'); if (slide) { editorSelected = true; show(slides.indexOf(slide)); stop(); } });
      on(root, 'shopify:block:deselect', () => { editorSelected = false; start(); });
      show(0); start();
    }
    if (root.matches('[data-jouri-popular], [data-jouri-featured]')) {
      const tabs = [...root.querySelectorAll('[role="tab"]')];
      const panels = [...root.querySelectorAll('[role="tabpanel"]')];
      function select(index, focus = false) {
        tabs.forEach((tab, i) => { tab.classList.toggle('is-active', i === index); tab.setAttribute('aria-selected', String(i === index)); tab.tabIndex = i === index ? 0 : -1; panels[i].hidden = i !== index; });
        if (focus) tabs[index].focus();
      }
      tabs.forEach((tab, i) => {
        on(tab, 'click', () => select(i));
        on(tab, 'keydown', event => {
          const targets = { ArrowRight: (i + 1) % tabs.length, ArrowLeft: (i - 1 + tabs.length) % tabs.length, Home: 0, End: tabs.length - 1 };
          if (!(event.key in targets)) return;
          event.preventDefault(); select(targets[event.key], true);
        });
      });
    }
    if (root.matches('[data-jouri-reviews]')) {
      const track = root.querySelector('[data-review-track]');
      const cards = [...track.querySelectorAll('figure')];
      const count = root.querySelector('[data-review-count]');
      let requested = 0;
      const current = () => Math.max(0, Math.min(cards.length - 1, Math.round(track.scrollLeft / (track.clientWidth || 1))));
      const sync = () => {
        if (!cards.length) return;
        const index = current();
        cards.forEach((card, i) => { card.inert = i !== index; card.setAttribute('aria-hidden', String(i !== index)); });
        if (count) count.textContent = `${index + 1} / ${cards.length}`;
      };
      const show = (index, instant = false) => {
        if (!cards.length) return;
        requested = (index + cards.length) % cards.length;
        track.scrollTo({ left: requested * track.clientWidth, behavior: instant || reduced.matches ? 'instant' : 'smooth' });
      };
      on(root.querySelector('[data-review-prev]'), 'click', () => show(requested - 1));
      on(root.querySelector('[data-review-next]'), 'click', () => show(requested + 1));
      on(track, 'scroll', sync);
      on(track, 'scrollend', () => { requested = current(); sync(); });
      on(track, 'pointerdown', () => { requested = current(); });
      on(track, 'keydown', event => {
        if (event.target !== track) return;
        const targets = { ArrowRight: requested + 1, ArrowLeft: requested - 1, Home: 0, End: cards.length - 1 };
        if (!(event.key in targets)) return;
        event.preventDefault(); show(targets[event.key]);
      });
      on(window, 'resize', () => show(requested, true));
      on(root, 'shopify:block:select', event => { const card = event.target.closest('figure'); if (card) show(cards.indexOf(card), true); });
      sync();
    }
    if (root.matches('[data-jouri-product]')) {
      const select = root.querySelector('[data-variant-select]');
      const price = root.querySelector('[data-variant-price]');
      const button = root.querySelector('[name="add"]');
      const compare = root.querySelector('[data-variant-compare]');
      on(select, 'change', () => {
        const option = select.selectedOptions[0];
        price.textContent = option.dataset.price;
        compare.textContent = option.dataset.compare || '';
        compare.hidden = !option.dataset.compare;
        button.disabled = option.dataset.available !== 'true';
        button.textContent = button.disabled ? root.dataset.soldLabel : root.dataset.addLabel;
        const url = new URL(location.href); url.searchParams.set('variant', option.value); history.replaceState(null, '', url);
      });
    }
    cleanups.set(root, () => { disposed = true; stop(); abort.abort(); cleanups.delete(root); });
  }
  const selector = '[data-jouri-hero], [data-jouri-popular], [data-jouri-featured], [data-jouri-reviews], [data-jouri-product]';
  const initialise = scope => scope.querySelectorAll(selector).forEach(init);
  initialise(document);
  document.addEventListener('shopify:section:load', event => initialise(event.target));
  document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll(selector).forEach(root => cleanups.get(root)?.()));

  // Quick view fetches Shopify's rendered product section: prices, variants and copy stay authoritative.
  let dialog, opener, request;
  document.addEventListener('click', async event => {
    const trigger = event.target.closest('[data-jouri-quick-view]');
    if (!trigger) return;
    opener = trigger;
    if (!dialog) {
      dialog = document.createElement('dialog'); dialog.className = 'j-quick-dialog jouri-scope';
      dialog.addEventListener('click', event => { if (event.target === dialog || event.target.closest('[data-quick-close]')) dialog.close(); });
      dialog.addEventListener('close', () => { request?.abort(); dialog.querySelectorAll(selector).forEach(root => cleanups.get(root)?.()); opener?.focus(); });
      document.body.append(dialog);
    }
    request?.abort(); request = new AbortController();
    const url = new URL(trigger.dataset.jouriQuickView, location.origin);
    const fallback = () => { dialog.close(); location.assign(url.pathname); };
    trigger.setAttribute('aria-busy', 'true');
    try {
      url.searchParams.set('section_id', 'jouri-quick-view');
      const response = await fetch(url, {signal: request.signal});
      if (!response.ok) throw new Error('Product could not be loaded');
      const html = await response.text();
      const parsed = new DOMParser().parseFromString(html, 'text/html');
      const content = parsed.querySelector('[data-quick-content]');
      if (!content) throw new Error('Missing product section');
      dialog.replaceChildren(content);
      dialog.setAttribute('aria-label', content.dataset.title);
      dialog.showModal();
    } catch (error) { if (error.name !== 'AbortError') fallback(); }
    finally { trigger.removeAttribute('aria-busy'); }
  });
})();
